import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { PagesImportPdfDocument } from 'generated/resources/graphql'
import { v4 as uuidv4 } from 'uuid'

import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FileItem } from './components/FileItem'
import { UploadArea } from './components/UploadArea'

type FileWithId = {
  id: string
  file: File
}

export type ImportedPage = {
  id: number
  title: string
}

export interface UploadHandle {
  canProceed: boolean
  handleNext: () => void
}

interface UploadProps {
  onNext: (importedPages: ImportedPage[]) => void
  folderType?: string
  setIsImporting: (isImporting: boolean) => void
  isImporting: boolean
  onCanProceedChange: (canProceed: boolean) => void
}

const removePdfExtension = (fileName: string): string => {
  return fileName.replace(/\.pdf$/i, '')
}

export const Upload = forwardRef<UploadHandle, UploadProps>(function Upload(
  { onNext, folderType, setIsImporting, onCanProceedChange },
  ref
) {
  const [selectedFiles, setSelectedFiles] = useState<FileWithId[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const handleNextRef = useRef<() => Promise<void>>(() => Promise.resolve())

  const { upload: uploadFile } = useDirectUpload({
    resourceType: 'HandbookPages::Page',
  })

  const [importPdf] = useMutation(PagesImportPdfDocument, {
    extractorFn: (data) => data.handbookPagesMutations.importPdfHandbookpagesImport,
  })

  const processFiles = (files: File[]) => {
    const pdfFiles = files.filter((file) => file.type === 'application/pdf')

    if (pdfFiles.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('pages.upload_files_modal.upload.errors.only_pdf_files'),
      })
      return
    }

    // Allow multiple PDF files
    const filesWithIds: FileWithId[] = pdfFiles.map((file) => ({
      id: uuidv4(),
      file: file,
    }))

    setSelectedFiles((prev) => [...prev, ...filesWithIds])
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    track(trackingEvents.pages.PagesDocumentsUploadStarted, {
      upload_type: 'open_drag_and_drop',
    })
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    processFiles(files)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    track(trackingEvents.pages.PagesDocumentsUploadStarted, {
      upload_type: 'click_upload_button',
    })
    const files = e.target.files ? Array.from(e.target.files) : []
    processFiles(files)
    e.target.value = ''
  }

  const handleDiscardFile = useCallback((fileId: string) => {
    setSelectedFiles((prev) => prev.filter((fileWithId) => fileWithId.id !== fileId))
  }, [])

  const handleNext = async () => {
    if (selectedFiles.length === 0) return
    setIsImporting(true)

    const importSingleFile = async (fileWithId: FileWithId) => {
      try {
        const uploadResult = await uploadFile(fileWithId.file)
        if (uploadResult.type !== 'success') return { success: false as const }

        const title = removePdfExtension(fileWithId.file.name)

        const result = await importPdf({
          variables: {
            filePath: uploadResult.signedId,
            folderType: folderType ?? 'workplace',
            visibility: 'private',
            title,
          },
        })

        const handler = handleResult(result, () => ({}))
        const importData = handler?.success ? handler.data?.import : undefined

        if (importData?.success && importData.page?.id) {
          return {
            success: true as const,
            pageId: importData.page.id,
            title,
          }
        }

        return { success: false as const }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : i18n.t('common.error')
        app.setFlash({ type: 'wrong', message: errorMessage })
        return { success: false as const }
      }
    }

    // Start ALL uploads and imports simultaneously
    const results = await Promise.allSettled(selectedFiles.map((file) => importSingleFile(file)))
    setIsImporting(false)

    const successfulImports = results
      .filter(
        (
          result
        ): result is PromiseFulfilledResult<{
          success: true
          pageId: number
          title: string
        }> =>
          result.status === 'fulfilled' && result.value !== null && result.value.success === true
      )
      .map((result) => result.value)

    if (successfulImports.length > 0) {
      track(trackingEvents.pages.PagesDocumentsUploaded, {
        documents_count: successfulImports.length,
      })
      const importedPages: ImportedPage[] = successfulImports.map((r) => ({
        id: r.pageId,
        title: r.title,
      }))

      setSelectedFiles([])
      onNext(importedPages)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('pages.upload_files_modal.upload.errors.failed_to_import_pdf'),
      })
    }
  }

  handleNextRef.current = handleNext

  useImperativeHandle(
    ref,
    () => ({
      canProceed: selectedFiles.length > 0,
      handleNext: () => {
        void handleNextRef.current()
      },
    }),
    [selectedFiles.length]
  )

  useEffect(() => {
    onCanProceedChange(selectedFiles.length > 0)
  }, [selectedFiles.length, onCanProceedChange])

  return (
    <Box gap="s8" height="full" minHeight="s300" padding="s16">
      <UploadArea
        isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onFileSelect={handleFileSelect}
      />
      {selectedFiles.length > 0 && (
        <Box
          borderRadius={{ all: 'abs016' }}
          border={{
            all: {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
          }}
          flexNoShrink
          gapDivider
        >
          {selectedFiles.map((fileWithId) => (
            <FileItem key={fileWithId.id} fileWithId={fileWithId} onDelete={handleDiscardFile} />
          ))}
        </Box>
      )}
    </Box>
  )
})
